import axios from 'axios';

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './tokenStorage';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

/** Absolute URL for an uploaded media file, handling both full and relative URLs. */
export const mediaUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  const base = process.env.REACT_APP_MEDIA_URL || API_BASE_URL;
  return `${base.replace(/\/$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
};

const client = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const subscribeToRefresh = (cb) => refreshSubscribers.push(cb);

const refreshAccessToken = async () => {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error('No refresh token');
  const { data } = await axios.post(`${API_BASE_URL}/api/auth/token/refresh/`, {
    refresh,
  });
  setTokens({ access: data.access, refresh: data.refresh });
  return data.access;
};

/** Called when refresh fails; components can register a logout callback. */
let onUnauthorized = null;
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

client.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isAuthEndpoint =
      original?.url?.includes('/api/auth/token/') ||
      original?.url?.includes('/api/auth/login/') ||
      original?.url?.includes('/api/auth/register/') ||
      original?.url?.includes('/api/auth/forgot-password/') ||
      original?.url?.includes('/api/auth/reset-password/') ||
      original?.url?.includes('/api/auth/invite-setup/');

    if (error.response?.status !== 401 || isAuthEndpoint || original?._retry) {
      return Promise.reject(error);
    }

    original._retry = true;

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeToRefresh((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          resolve(client(original));
        });
      });
    }

    isRefreshing = true;
    try {
      const token = await refreshAccessToken();
      onRefreshed(token);
      original.headers.Authorization = `Bearer ${token}`;
      return await client(original);
    } catch (refreshError) {
      clearTokens();
      onUnauthorized?.();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/** Normalize API/network errors into a readable message + field errors. */
export const extractError = (error) => {
  const status = error?.response?.status;
  const detail = error?.response?.data;

  // Friendly labels for common HTTP errors (nginx/Django).
  const HTTP_MESSAGES = {
    413: 'File is too large for the server. Try a smaller file.',
    400: 'Invalid request. Please check your input.',
    401: 'You are not logged in. Please log in again.',
    403: 'You do not have permission to do this.',
    404: 'The requested resource was not found.',
    500: 'Server error. Please try again later.',
    502: 'Server is temporarily unavailable. Please try again.',
    503: 'Service is currently unavailable. Please try again later.',
  };

  // If the server returned a friendly HTTP error code, use that first.
  if (HTTP_MESSAGES[status]) {
    // Try to extract a more specific message from the response body.
    let bodyMsg = '';
    if (typeof detail === 'string') {
      bodyMsg = detail;
    } else if (detail?.detail && typeof detail.detail === 'string') {
      // Phase 18: only primitive details are shown; structured detail bodies
      // (field errors etc.) stay out of the generic message and surface via
      // `errors` so no raw [object Object] ever leaks to the UI.
      bodyMsg = detail.detail;
    } else if (detail?.message) {
      bodyMsg = detail.message;
    } else if (detail && typeof detail === 'object') {
      const firstVal = Object.values(detail)[0];
      bodyMsg = Array.isArray(firstVal) ? firstVal[0] : String(firstVal);
    }
    // Use body message only if it adds info beyond the generic status text.
    const msg = bodyMsg && !bodyMsg.includes(String(status)) ? `${HTTP_MESSAGES[status]} ${bodyMsg}` : HTTP_MESSAGES[status];
    return { message: msg, errors: detail?.errors || {}, status, code: detail?.code };
  }

  // JSON error body with a `message` field.
  if (detail?.message) {
    return {
      message: detail.message,
      errors: detail.errors || {},
      status,
      code: detail.code,
    };
  }

  // JSON error body with field-level errors (DRF style).
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const fields = {};
    Object.keys(detail).forEach((key) => {
      const val = detail[key];
      fields[key] = Array.isArray(val) ? val[0] : val;
    });
    return { message: Object.values(fields)[0] || 'Request failed', errors: fields, status };
  }

  // String or unknown body (e.g. nginx HTML error page).
  const bodyText = typeof detail === 'string' ? detail.substring(0, 200) : '';

  return {
    message: status ? `Request failed (${status}). ${bodyText}` : (error?.message || 'Network error. Is the server running?'),
    errors: {},
    status,
  };
};

export default client;
